import React from 'react';
import EditableField from '../EditableField';
import { formatCurrencyBR } from '../../utils/currency';
import { isServiceSector, isPlaceholderFisico, labelsFisicos } from '../../utils/setor';

/**
 * Seção com os dados extraídos da NC (cliente, produto, lote, data)
 * @param {object} dadosExtraidos - Dados extraídos da NC (extração inicial da IA)
 * @param {object} nc             - Objeto NC completo (colunas SQL — fonte de verdade após edição)
 * @param {function} onUpdate     - Callback chamado após save bem-sucedido (parent refetch)
 */
function NCResultDataSection({ dadosExtraidos, nc, onUpdate }) {
  const isExterna = nc?.origem_tipo === 'cliente';
  const ncId = nc?.id;

  // Cliente vem da extração da IA (ou cliente_emissor em NC externa) — não editável aqui.
  const clienteExibir =
    (isExterna && nc?.cliente_emissor) ||
    dadosExtraidos?.cliente ||
    'N/A';

  // Sprint-2 hotfix: prioriza coluna SQL (atualizada via PUT /editar) sobre
  // dados_extraidos (extração inicial da IA, pode estar desatualizada).
  const produtoExibir = nc?.produto || dadosExtraidos?.produto || '';
  const loteExibir = nc?.lote || dadosExtraidos?.lote || '';

  // #264/#264b — sinal de serviço da TELA. #264 oculta Produto/Lote vazios;
  // #264b renomeia os rótulos quando PREENCHIDOS num serviço (Serviço / Processo
  // · Período / Referência).
  // #270 — a DECISÃO passou a vir do backend (nc.is_servico), computada pela
  // industria EFETIVA (NC.industria → fallback Client.industria). Assim a tela
  // herda o setor da CONTA mesmo quando a NC não tem industria própria. O
  // isServiceSector(nc.industria) local fica só como FALLBACK pra backend antigo
  // que ainda não envia o sinal (mantém o comportamento #264/#264b anterior).
  const isServico =
    typeof nc?.is_servico === 'boolean'
      ? nc.is_servico
      : isServiceSector(nc?.industria);
  const rotulosFis = labelsFisicos(isServico);

  return (
    <section className="result-section">
      <h3><span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>fact_check</span>Dados Identificados</h3>
      <div className="info-grid">
        <div className="info-card">
          <label>Cliente</label>
          <p>{clienteExibir}</p>
        </div>
        {/* #264 — em setor de SERVIÇO, oculta Produto/Lote físicos quando
            vazios (não há produto/lote a identificar num serviço). Se o usuário
            preencheu, mantém editável. Setor industrial: inalterado.
            (#181-p2 já ocultava o Lote nos serviços; agora idem pro Produto
            quando vazio, e o Lote só some quando também está vazio.) */}
        {!(isServico && isPlaceholderFisico(produtoExibir)) && (
          <div className="info-card">
            <EditableField
              label={rotulosFis.produto}
              value={produtoExibir}
              campo="produto"
              ncId={ncId}
              disabled={!ncId}
              onUpdate={onUpdate}
            />
          </div>
        )}
        {!(isServico && isPlaceholderFisico(loteExibir)) && (
          <div className="info-card">
            <EditableField
              label={rotulosFis.lote}
              value={loteExibir}
              campo="lote"
              ncId={ncId}
              disabled={!ncId}
              onUpdate={onUpdate}
            />
          </div>
        )}
        <div className="info-card">
          <label>Data</label>
          <p>{dadosExtraidos?.data || 'N/A'}</p>
        </div>
        {/* #51 (Sessão 32): custo estimado (R$). Sempre sinalizado — quando vazio,
            "Custo não estimado" (não esconder = não vira feature invisível). Editar no NCEditor. */}
        <div className="info-card">
          <label>Custo estimado</label>
          <p>
            {nc?.custo_estimado != null
              ? `R$ ${formatCurrencyBR(nc.custo_estimado)}`
              : <span style={{ color: '#94a3b8' }}>Custo não estimado</span>}
          </p>
        </div>
        {/* #108: categoria(s) de impacto (read-only aqui; edição no NCEditor). */}
        <div className="info-card">
          <label>Categoria de impacto</label>
          <p>
            {Array.isArray(nc?.categoria_impacto) && nc.categoria_impacto.length > 0
              ? nc.categoria_impacto
                  .map((c) => ({ financeiro: 'Financeiro', reputacional: 'Reputacional', operacional: 'Operacional' }[c] || c))
                  .join(', ')
              : <span style={{ color: '#94a3b8' }}>Não classificado</span>}
          </p>
        </div>
      </div>

      {isExterna && (
        <>
          <h4 style={{ marginTop: '20px', marginBottom: '8px', fontSize: '14px', color: '#57657a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px', textTransform: 'none' }}>apartment</span>Origem externa — emitida pelo cliente
          </h4>
          <div className="info-grid">
            {nc?.cliente_emissor && (
              <div className="info-card">
                <label>Cliente emissor</label>
                <p>{nc.cliente_emissor}</p>
              </div>
            )}
            {nc?.numero_nc_cliente && (
              <div className="info-card">
                <label>Nº NC no cliente</label>
                <p>{nc.numero_nc_cliente}</p>
              </div>
            )}
            {nc?.prazo_resposta_cliente && (
              <div className="info-card">
                <label>Prazo de resposta</label>
                <p>{nc.prazo_resposta_cliente}</p>
              </div>
            )}
            {nc?.contato_cliente_email && (
              <div className="info-card">
                <label>Contato</label>
                <p>{nc.contato_cliente_email}</p>
              </div>
            )}
            {nc?.arquivo_original_nome && (
              <div className="info-card">
                <label>Arquivo original</label>
                <p>{nc.arquivo_original_nome}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/*
        TD-138 REABERTO (Sessão 13.5): contexto técnico + indústria.
        Renderização read-only — só aparece se preenchido (não polui tela
        para NCs antigas pré-feature). Edição via NCEditor é
        TD-followup-editar-contexto-NCEditor Sprint 5+.
        Backend agora serializa esses 3 campos top-level (schemas.py +
        _get_nc_impl + /processar-completa-sync) após fix do gap onde
        Pydantic NCResponse silenciosamente dropava.
      */}
      {(nc?.contexto_processo || nc?.contexto_setorial_custom || nc?.industria) && (
        <>
          <h4 style={{ marginTop: '20px', marginBottom: '8px', fontSize: '14px', color: '#57657a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px', textTransform: 'none' }}>biotech</span>Contexto técnico
          </h4>
          <div className="info-grid">
            {nc?.industria && (
              <div className="info-card">
                <label>Indústria / Setor</label>
                <p>{nc.industria}</p>
              </div>
            )}
            {nc?.contexto_processo && (
              <div className="info-card" style={{ gridColumn: 'span 2' }}>
                <label>Situação atual do processo</label>
                <p style={{ whiteSpace: 'pre-wrap' }}>{nc.contexto_processo}</p>
              </div>
            )}
            {nc?.contexto_setorial_custom && (
              <div className="info-card" style={{ gridColumn: 'span 2' }}>
                <label>Contexto da aplicação no cliente</label>
                <p style={{ whiteSpace: 'pre-wrap' }}>{nc.contexto_setorial_custom}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* #77 Felipe (Sessão 27) — Observações internas livres do usuário.
          Distinta dos campos de contexto técnico (que alimentam IA): notas
          internas, lembretes, follow-ups. Aparece em fundo amarelo discreto
          pra deixar claro que é "post-it interno". Só renderiza se preenchido. */}
      {nc?.observacoes && (
        <>
          <h4 style={{ marginTop: '20px', marginBottom: '8px', fontSize: '14px', color: '#57657a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px', textTransform: 'none' }}>edit_note</span>Observações internas
          </h4>
          <div
            style={{
              background: '#fef9c3',
              borderLeft: '3px solid #ca8a04',
              borderRadius: '4px',
              padding: '12px 16px',
              fontSize: '14px',
              color: '#713f12',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.5,
            }}
          >
            {nc.observacoes}
          </div>
        </>
      )}
    </section>
  );
}

export default NCResultDataSection;
